import React, { useMemo } from 'react';
import ConsentimientoInformadoSection from './ConsentimientoInformadoSection';
import MspRegistroReadView from './MspRegistroReadView';
import ProfesionalAtencionCard from './ProfesionalAtencionCard';
import { sanitizeMedicamentosFilas } from './FichaOdontologicaForm';
import {
  resumenAlergiasLectura,
  resumenEnfermedadesLectura,
  resumenHabitosLectura,
  getSignosVitalesPairs,
  getAntecedentesOdontoPairs,
  getExamenEstomaPairs,
} from './fichaOdontologicaLecturaHelpers';
import {
  ClinicalSection,
  ClinicalSubsection,
  InfoCard,
  InfoGrid,
  NarrativeBlock,
  KeyValueTable,
  MedicamentosTable,
  HosIndexChips,
  SectionEmpty,
  StatusPill,
  hasValue,
} from './FichaLecturaUi';

function formatFecha(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return String(iso);
  }
}

function formatFechaHora(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-EC', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return String(iso);
  }
}

function countPairsWithValue(pairs) {
  return (pairs || []).filter((p) => hasValue(p.value)).length;
}

function sectionHasMspRows(rows) {
  return Array.isArray(rows) && rows.some((r) => Object.values(r || {}).some((v) => hasValue(v)));
}

export default function FichaHistorialClinicaView({ form, idDoctor, tenantId = null, onEditSection = null, onGoToMspTratamiento = null }) {
  const edit = (sectionId) => (onEditSection ? () => onEditSection(sectionId) : null);
  const alergias = resumenAlergiasLectura(form, idDoctor);
  const enfermedades = resumenEnfermedadesLectura(form, idDoctor);
  const habitos = resumenHabitosLectura(form, idDoctor);
  const medFilas = sanitizeMedicamentosFilas(form.medicamentosActualesFilas);

  const signosPairs = useMemo(() => getSignosVitalesPairs(form, idDoctor), [form, idDoctor]);
  const antecedentesPairs = useMemo(() => getAntecedentesOdontoPairs(form, idDoctor), [form, idDoctor]);
  const examenPairs = useMemo(() => getExamenEstomaPairs(form, idDoctor), [form, idDoctor]);

  const medicaFilled = useMemo(() => {
    const n =
      (hasValue(alergias) ? 1 : 0) +
      (medFilas.some((r) => r.nombre || r.dosis) || hasValue(form.medicamentosActuales) ? 1 : 0) +
      (hasValue(enfermedades) ? 1 : 0) +
      (hasValue(form.cirugiasPrevias) ? 1 : 0) +
      (hasValue(habitos) ? 1 : 0) +
      (hasValue(form.embarazo) ? 1 : 0) +
      (hasValue(form.vacunaciones) ? 1 : 0) +
      (hasValue(form.grupoSanguineo) ? 1 : 0) +
      (hasValue(form.historialMedico) ? 1 : 0);
    return n;
  }, [form, alergias, enfermedades, habitos, medFilas]);

  const odontoFilled = useMemo(() => {
    return (
      (hasValue(form.motivoConsulta) ? 1 : 0) +
      (hasValue(form.enfermedadActual) ? 1 : 0) +
      countPairsWithValue(signosPairs) +
      countPairsWithValue(antecedentesPairs) +
      countPairsWithValue(examenPairs) +
      (hasValue(form.fechaUltimaVisitaOdontologica) ? 1 : 0) +
      (hasValue(form.tratamientosPrevios) ? 1 : 0) +
      (hasValue(form.higieneBucal) ? 1 : 0) +
      (hasValue(form.dolorSintomasActuales) ? 1 : 0) +
      (hasValue(form.intensidadDolor) ? 1 : 0) +
      (hasValue(form.enfermedadesBucalesCronicas) ? 1 : 0) +
      (hasValue(form.protesisAparatos) ? 1 : 0)
    );
  }, [form, signosPairs, antecedentesPairs, examenPairs]);

  const consentOk = !!form.consentimientoInformado;
  const mspDiagRows = sectionHasMspRows(form.tablaDiagnosticosMSP);
  const mspTtoRows = sectionHasMspRows(form.tablaTratamientosMSP);

  const mspFilled = useMemo(() => {
    let n = 0;
    if (hasValue(form.placa) || hasValue(form.calculo) || hasValue(form.gingivitis)) n++;
    if (hasValue(form.enfermedadPeriodontal) || hasValue(form.angleClasificacion) || hasValue(form.fluorosis)) n++;
    if (hasValue(form.biometria) || hasValue(form.quimicaSanguinea) || hasValue(form.rayosX)) n++;
    if (hasValue(form.planDiagTerapeuticoEducativoMSP) || hasValue(form.tratamientoMSPTextoResumen)) n++;
    if (mspDiagRows) n++;
    if (mspTtoRows) n++;
    return n;
  }, [form, mspDiagRows, mspTtoRows]);

  const seguroFilled = useMemo(() => {
    return (
      (hasValue(form.tipoSeguro) ? 1 : 0) +
      (hasValue(form.seguro) ? 1 : 0) +
      (hasValue(form.numeroSeguro) ? 1 : 0) +
      (hasValue(form.fechaRegistro) ? 1 : 0) +
      (hasValue(form.notas) ? 1 : 0)
    );
  }, [form]);

  return (
    <div className="fo-historial">
      <div className="fo-historial-head">
        <h3 className="fo-historial-title">
          <i className="bi bi-journal-medical" />
          Historia clínica y registros MSP
        </h3>
        <p className="fo-historial-desc">
          Antecedentes médicos, odontológicos, consentimiento, indicadores y datos administrativos. Use{' '}
          <strong>Completar</strong> o <strong>Editar</strong> en cada bloque para abrir solo esa parte de la ficha.
        </p>
        {onEditSection ? (
          <button type="button" className="cp-btn cp-btn--ghost cp-btn--sm fo-historial-edit-all" onClick={() => onEditSection('medica')}>
            <i className="bi bi-pencil-square" />
            Editar toda la historia clínica
          </button>
        ) : null}
      </div>

      <div className="fo-historial-sections">
        <ClinicalSection
          sectionId="medica"
          title="Historia médica general"
          subtitle="Alergias, medicación, enfermedades crónicas y antecedentes"
          icon="bi-heart-pulse-fill"
          accentColor="#FA896B"
          defaultOpen
          badge={medicaFilled > 0 ? medicaFilled : null}
          hasData={medicaFilled > 0}
          onEditSection={onEditSection}
        >
          {medicaFilled === 0 ? (
            <SectionEmpty
              icon="bi-heart"
              message="No hay antecedentes médicos registrados."
              onComplete={edit('medica')}
            />
          ) : (
            <>
              <NarrativeBlock
                label="Alergias"
                value={alergias}
                icon="bi-exclamation-triangle-fill"
                highlight
                variant="alert"
              />
              <MedicamentosTable filas={medFilas} legacy={form.medicamentosActuales} />
              <ClinicalSubsection title="Condiciones y hábitos" icon="bi-activity">
                <InfoGrid>
                  <InfoCard label="Enfermedades crónicas" value={enfermedades} icon="bi-virus" fullWidth />
                  <InfoCard label="Cirugías previas" value={form.cirugiasPrevias} icon="bi-scissors" fullWidth />
                  <InfoCard label="Hábitos relevantes" value={habitos} icon="bi-sliders" fullWidth />
                </InfoGrid>
              </ClinicalSubsection>
              <ClinicalSubsection title="Otros antecedentes" icon="bi-clipboard2-pulse">
                <InfoGrid>
                  <InfoCard label="Embarazo" value={form.embarazo} icon="bi-gender-female" />
                  <InfoCard label="Trimestre" value={form.embarazoTrimestre} icon="bi-calendar3" />
                  <InfoCard label="Vacunaciones" value={form.vacunaciones} icon="bi-shield-plus" />
                  <InfoCard label="Grupo sanguíneo y Rh" value={form.grupoSanguineo} icon="bi-droplet-half" />
                </InfoGrid>
                {hasValue(form.historialMedico) && (
                  <NarrativeBlock label="Historial médico (notas)" value={form.historialMedico} icon="bi-journal-text" />
                )}
              </ClinicalSubsection>
            </>
          )}
        </ClinicalSection>

        <ClinicalSection
          sectionId="odontologica"
          title="Historia odontológica"
          subtitle="Motivo de consulta, signos vitales y examen estomatognático"
          icon="bi-tooth-fill"
          accentColor="#13DEB9"
          defaultOpen
          badge={odontoFilled > 0 ? odontoFilled : null}
          hasData={odontoFilled > 0}
          onEditSection={onEditSection}
        >
          {odontoFilled === 0 ? (
            <SectionEmpty
              icon="bi-tooth"
              message="No hay historia odontológica registrada."
              onComplete={edit('odontologica')}
            />
          ) : (
            <>
              <ClinicalSubsection title="Consulta actual" icon="bi-chat-left-text">
                <NarrativeBlock label="Motivo de consulta" value={form.motivoConsulta} icon="bi-chat-quote" />
                <NarrativeBlock label="Enfermedad o problema actual" value={form.enfermedadActual} icon="bi-clipboard-pulse" />
              </ClinicalSubsection>
              <ClinicalSubsection title="Evaluación clínica" icon="bi-clipboard2-check">
                <KeyValueTable title="Signos vitales" pairs={signosPairs} icon="bi-heart-pulse" />
                <KeyValueTable
                  title="Antecedentes personales y familiares"
                  pairs={antecedentesPairs}
                  icon="bi-people"
                />
                <KeyValueTable
                  title="Examen sistema estomatognático"
                  pairs={examenPairs}
                  icon="bi-body-text"
                />
              </ClinicalSubsection>
              <ClinicalSubsection title="Historial y síntomas" icon="bi-clock-history">
                <InfoGrid>
                  <InfoCard
                    label="Última visita odontológica"
                    value={formatFecha(form.fechaUltimaVisitaOdontologica)}
                    icon="bi-calendar-check"
                  />
                  <InfoCard label="Higiene bucal" value={form.higieneBucal} icon="bi-brush" />
                  <InfoCard label="Intensidad del dolor (1-10)" value={form.intensidadDolor} icon="bi-speedometer2" />
                </InfoGrid>
                <NarrativeBlock label="Tratamientos previos" value={form.tratamientosPrevios} icon="bi-bandaid" />
                <NarrativeBlock label="Dolor o síntomas actuales" value={form.dolorSintomasActuales} icon="bi-emoji-frown" />
                <NarrativeBlock
                  label="Enfermedades bucales crónicas"
                  value={form.enfermedadesBucalesCronicas}
                  icon="bi-virus2"
                />
                <NarrativeBlock label="Prótesis o aparatos" value={form.protesisAparatos} icon="bi-gear" />
              </ClinicalSubsection>
            </>
          )}
        </ClinicalSection>

        <ClinicalSection
          sectionId="consentimiento"
          title="Consentimiento informado"
          subtitle="Plantilla del consultorio con datos del paciente y firma"
          icon="bi-pen-fill"
          accentColor="#7460EE"
          badge={consentOk ? 'Firmado' : null}
          hasData={consentOk || hasValue(form.consentimientoInformadoFirmaDataUrl) || hasValue(form.consentimientoInformadoTexto)}
          onEditSection={onEditSection}
        >
          <div className="fo-consent-wrap">
            <div className="fo-consent-status">
              <StatusPill ok={consentOk} labelOk="Consentimiento aceptado" labelNo="Sin aceptar" />
              {hasValue(form.consentimientoInformadoFecha) && (
                <span className="fo-consent-fecha">
                  <i className="bi bi-calendar3" />
                  {formatFecha(form.consentimientoInformadoFecha)}
                </span>
              )}
            </div>
            <ConsentimientoInformadoSection form={form} readOnly tenantId={tenantId} idDoctor={idDoctor} />
          </div>
        </ClinicalSection>

        <ClinicalSection
          sectionId="msp"
          title="Indicadores MSP y planes"
          subtitle="Índices de salud bucal, diagnóstico, tratamiento y profesional"
          icon="bi-clipboard2-data-fill"
          accentColor="#5D87FF"
          badge={mspFilled > 0 ? mspFilled : null}
          hasData={mspFilled > 0}
          onEditSection={onEditSection}
        >
          <p className="fo-msp-intro">
            <i className="bi bi-info-circle" />
            Vista de lectura. Para editar H.O.S., CPO-ceo, planes y periodontal, use la sección{' '}
            <strong>Indicadores MSP y planes</strong> en esta ficha o el modal de edición.
          </p>

          <MspRegistroReadView
            form={form}
            showEditHint={!onEditSection && !onGoToMspTratamiento}
            showFillGuide={false}
            onEditFicha={onEditSection ? () => onEditSection('msp') : null}
            onAddTratamiento={onGoToMspTratamiento}
            showProfesional={false}
          />

          <ClinicalSubsection title="Exámenes complementarios" icon="bi-microscope">
            <InfoGrid>
              <InfoCard label="Biometría" value={form.biometria} icon="bi-rulers" />
              <InfoCard label="Química sanguínea" value={form.quimicaSanguinea} icon="bi-droplet" />
              <InfoCard label="Rayos X" value={form.rayosX} icon="bi-radioactive" />
              <InfoCard label="Otros exámenes" value={form.otrosExamenes} icon="bi-plus-square" />
            </InfoGrid>
            <NarrativeBlock
              label="Plan diagnóstico · terapéutico · educativo"
              value={form.planDiagTerapeuticoEducativoMSP}
              icon="bi-list-check"
            />
            <NarrativeBlock
              label="Tratamiento (resumen clínico)"
              value={form.tratamientoMSPTextoResumen}
              icon="bi-clipboard2-pulse"
            />
          </ClinicalSubsection>

          <ClinicalSubsection title="Profesional que atiende" icon="bi-person-badge">
            <ProfesionalAtencionCard compact />
          </ClinicalSubsection>
        </ClinicalSection>

        <ClinicalSection
          sectionId="seguro"
          title="Seguro y administrativa"
          subtitle="Cobertura, póliza y notas del expediente"
          icon="bi-shield-check"
          accentColor="#FFAE1F"
          badge={seguroFilled > 0 ? seguroFilled : null}
          hasData={seguroFilled > 0}
          onEditSection={onEditSection}
        >
          {seguroFilled === 0 ? (
            <SectionEmpty
              icon="bi-shield"
              message="No hay datos de seguro ni notas administrativas."
              onComplete={edit('seguro')}
            />
          ) : (
            <InfoGrid>
              <InfoCard label="Tipo de seguro" value={form.tipoSeguro} icon="bi-shield" highlight />
              <InfoCard label="Nombre del seguro" value={form.seguro} icon="bi-building" />
              <InfoCard label="Número de póliza" value={form.numeroSeguro} icon="bi-hash" />
              <InfoCard
                label="Fecha de registro"
                value={formatFechaHora(form.fechaRegistro)}
                icon="bi-calendar-plus"
              />
              <InfoCard label="Notas adicionales" value={form.notas} icon="bi-sticky" fullWidth />
            </InfoGrid>
          )}
        </ClinicalSection>
      </div>
    </div>
  );
}
